import { FC, ReactNode, useState } from 'react';

import Button from '@components/buttons/Button';
import { Input } from '@components/form/Input';
import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { sanitizeInstagramUsername } from '@utilities/sanitizeInstagramUsername';

import { PREFILLED_INSTAGRAM_HANDLE_KEY } from '@screens/LogInScreen/EntertainerLoginContent';

interface InstagramHandlePopupContentProps {
  title?: string;
  content?: ReactNode;
  onClose: () => void;
  onSubmit: (instagramHandle: string) => void;
}

interface InstagramHandlePopupProps extends InstagramHandlePopupContentProps {
  isVisible: boolean;
}

const PopupContent: FC<InstagramHandlePopupContentProps> = ({
  title = "What's your Instagram?",
  content = 'We use your Instagram handle to save your application and tag you in your feature.',
  onClose,
  onSubmit,
}) => {
  const [instagramHandle, setInstagramHandle] = useState('');
  const [error, setError] = useState<string | undefined>();

  const handleSubmit = () => {
    if (!instagramHandle) {
      setError('Instagram handle is required.');
      return;
    }

    localStorage.setItem(PREFILLED_INSTAGRAM_HANDLE_KEY, instagramHandle);
    onSubmit(instagramHandle);
    onClose();
  };

  return (
    <div>
      <TitleToolbar text={title} size="sm" />
      <p className="mt-0">{content}</p>
      <Input
        placeholder="e.g. your.instagram.handle"
        name="instagramHandle"
        value={instagramHandle}
        onChange={(event) => {
          setError(undefined);
          setInstagramHandle(sanitizeInstagramUsername(event.target.value));
        }}
      />
      {error && <p className="text-danger mt-1 mb-0">{error}</p>}
      <Button
        data-testId="instagram-handle-popup-continue-button"
        className="mt-2"
        roundness="rounded"
        disabled={!instagramHandle}
        onClick={handleSubmit}
      >
        Continue
      </Button>
    </div>
  );
};

export const InstagramHandlePopup: FC<InstagramHandlePopupProps> = ({ isVisible, ...props }) => {
  return (
    <Popup isVisible={isVisible} onClose={props.onClose}>
      <Popup.Content>{isVisible && <PopupContent {...props} />}</Popup.Content>
    </Popup>
  );
};
